import { VANILLA, VANILLA_STRIP } from '@approot/constants';
import {
  GenericLeg,
  ProductName,
  ProductSpecification,
  QuotePair,
  RfqTabLegs,
  UserSettings
} from '@approot/models';
import { clone, crossToPair } from '@approot/utilities';
import dayjs from 'dayjs';
import { singleLegTitleMapper } from './rfq-tab/rfq-tab';
import { RFQTabTitle, RfqBuilderLeg } from './rfq.types';

export function rfqTabTitleMapper(
  legs: RfqBuilderLeg[],
  userSettings: UserSettings,
  isDirectRfq = true
): RFQTabTitle {
  // return the title of the tab
  if (!legs.length) {
    return null;
  }

  if (legs.length === 1) {
    return singleLegTitleMapper(legs[0].productType, userSettings.usePerseus);
  }

  const leg1 = legs[0];
  const leg1ProductType = leg1.productType;
  const leg1Cross = leg1.product.cross;
  const leg1Position = leg1.product.position;
  const leg2Position = legs[1].product.position;
  let isThereABuyOrEnter = false; // all legs cant be sell

  if (legs.length > 1 && leg1Position !== 'Buy' && !isDirectRfq) {
    return null;
  }

  const allLegsSimilar = legs.every(legData => {
    const position = legData.product.position;
    if (position === 'Buy' || position === 'Enter') {
      isThereABuyOrEnter = true;
    }
    return (
      legData.productType === leg1.productType && // all product types the same
      leg1Cross === legData.product.cross // all crosses the same
    );
  });

  if (
    userSettings.parent['rfq:multiLegs:allowVanillaStrips'] &&
    !allLegsSimilar
  ) {
    const everyLegVanillaOrStrip = legs.every(
      leg =>
        (leg.productType === VANILLA_STRIP || leg.productType === VANILLA) &&
        leg1Cross === leg.product.cross
    );

    if (!everyLegVanillaOrStrip) {
      return null;
    }
  } else if (!allLegsSimilar) {
    return null;
  }

  if ((isThereABuyOrEnter && !isDirectRfq) || isDirectRfq) {
    if (
      legs.length === 2 &&
      (leg1ProductType === 'vanilla' || leg1ProductType === 'ndo') &&
      leg1Position !== leg2Position
    ) {
      return spreadTitleMapper(leg1ProductType);
    }
    return multiLegTitleMapper(leg1ProductType, userSettings, legs);
  }
  return null;
}

export function spreadTitleMapper(firstLegProductType: string): ProductName {
  switch (firstLegProductType) {
    case 'vanilla':
      return { shortName: 'spread', longName: 'Spread' };
    case 'ndo':
      return { shortName: 'ndoSpread', longName: 'NDO Spread' };
    default:
      return null;
  }
}

export function multiLegTitleMapper(
  firstLegProductType: string,
  userSettings: UserSettings,
  legs: RfqBuilderLeg[]
): ProductName {
  const usePerseus = userSettings.usePerseus;
  const allProductTypesTheSame = legs.every(
    leg => leg.productType === legs[0].productType
  );
  if (!allProductTypesTheSame) {
    if (
      legs.every(
        leg => leg.productType === VANILLA_STRIP || leg.productType === VANILLA
      )
    ) {
      return {
        shortName: 'vanilla+VanillaStripMultiLeg',
        longName: 'Vanilla + Vanilla Strip Multi-Leg'
      };
    }
  }

  switch (firstLegProductType) {
    case 'vanilla':
      return {
        shortName: 'vanillaMultiLeg',
        longName: 'Vanilla Multi-Leg'
      };
    case 'ndo':
      return {
        shortName: 'ndoMultiLeg',
        longName: 'NDO Multi-Leg'
      };
    case VANILLA_STRIP: {
      if (userSettings.parent['rfq:multiLegs:allowVanillaStrips']) {
        return {
          shortName: 'vanillaStripMultiLeg',
          longName: 'Vanilla Strip Multi-Leg'
        };
      } else {
        return null;
      }
    }
    default:
      return usePerseus
        ? {
            shortName: 'custom',
            longName: 'Custom'
          }
        : null;
  }
}

export function isFirstGenExotic(productName: string): boolean {
  switch (productName) {
    case 'ki':
    case 'ko':
    case 'erko':
    case 'erki':
    case 'oneTouch':
    case 'noTouch':
    case 'oneTouchKnockOut':
    case 'instantOneTouch':
    case 'doubleNoTouch':
    case 'doubleOneTouch':
    case 'digital':
    case 'doubleKnockIn':
    case 'doubleKnockOut':
      return true;
    default:
      return false;
  }
}

export function rfqLegs(
  legs: RfqBuilderLeg[],
  staticTab: boolean,
  newRfqTab: boolean,
  productSpecifications: ProductSpecification[]
): RfqTabLegs[] {
  if (!legs) {
    return [];
  }

  return legs.map(leg => {
    let legToUse = clone(leg.product);

    if (leg.product.position) {
      legToUse.position = leg.product.position;
    }

    if (staticTab && legs.length === 1 && legToUse.position) {
      // position should be reset to buy/Enter
      const positions = productSpecifications
        .find(spec => spec.productType.shortName === leg.productType)
        .attributes.find(attr => attr.shortName === 'position')?.value;

      if (positions) {
        legToUse.position = positions[0];
      }
    }
    return {
      leg: legToUse,
      shortName: leg.productType,
      fieldsToBeEdited: leg.fieldsToBeEdited,
      childLeg: leg.childVanillaOption
    };
  });
}

export function getTabTitleCross(firstLeg: RfqBuilderLeg): QuotePair {
  if (!firstLeg) {
    return null;
  }
  const firstLegValue = firstLeg.childVanillaOption || firstLeg.product;

  return firstLegValue?.cross ? crossToPair(firstLegValue.cross) : null;
}

export function setPositionToUse(
  legs: RfqBuilderLeg[],
  userSettings: UserSettings
): string {
  const productShortName = rfqTabTitleMapper(legs, userSettings)?.shortName;
  if (
    productShortName === 'simpleTarf' ||
    productShortName === 'tpf' ||
    productShortName === 'simplePivotTarf'
  ) {
    return 'Enter';
  }
  return null;
}

export function calcSideToShowOneWay(
  rfqName: string,
  legs: RfqBuilderLeg[]
): 'bid' | 'offer' {
  // calculates which side to show in one way mode
  if (
    (legs?.length === 1 &&
      (legs[0].product.position === 'Unwind' || // and the position is sell or unwind
        legs[0].product.position === 'Sell')) ||
    rfqName === 'dcd'
  ) {
    return 'bid';
  }
  return 'offer';
}

export function isAnExpDateInThePast(legs: GenericLeg[]): boolean {
  if (!legs.length) {
    return false;
  }

  const currDate = dayjs(new Date()).startOf('day').valueOf();
  return !!legs.find(
    leg => leg.expiryDate && new Date(leg.expiryDate).valueOf() < currDate
  );
}
